import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import BrimiLogo from "@/components/BrimiLogo";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      const from = location.state?.from || "/dashboard";
      nav(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] grid-noise flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="login-logo-link">
          <BrimiLogo size={44} withBadge showText textColor="white" />
        </Link>

        <div className="brimi-card p-8">
          <h1 className="font-display text-3xl text-slate-900">Welcome back</h1>
          <p className="text-slate-500 mt-1">Sign in to continue your career journey.</p>

          <button
            data-testid="login-google-button"
            onClick={loginWithGoogle}
            className="mt-6 w-full flex items-center justify-center gap-3 py-3 rounded-full border-2 border-slate-200 hover:bg-slate-50 font-bold text-slate-800 transition"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-[0.2em] text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Email</label>
              <div className="mt-1.5 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-electric-500 focus-within:ring-2 focus-within:ring-blue-100">
                <Mail className="w-4 h-4 text-slate-400" />
                <input
                  data-testid="login-email-input"
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.edu"
                  className="flex-1 bg-transparent outline-none text-slate-900"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Password</label>
              <div className="mt-1.5 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-electric-500 focus-within:ring-2 focus-within:ring-blue-100">
                <Lock className="w-4 h-4 text-slate-400" />
                <input
                  data-testid="login-password-input"
                  type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent outline-none text-slate-900"
                />
              </div>
            </div>
            <button
              data-testid="login-submit-button"
              type="submit" disabled={loading}
              className="w-full brimi-pill accent-coral flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-sm text-slate-500 text-center mt-6">
            New here?{" "}
            <Link to="/register" data-testid="login-goto-register" className="font-bold text-electric-600" style={{ color: "#3B82F6" }}>Create an account</Link>
          </p>
        </div>

        <p className="text-xs text-slate-500 text-center mt-6">
          Test account: <span className="text-slate-400">test.student@brimi.ai / TestStudent@123</span>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h5.9c-.3 1.4-1.1 2.6-2.3 3.4v2.8h3.7c2.2-2 3.2-4.9 3.2-8.3z"/>
      <path fill="#34A853" d="M12 23c3.1 0 5.7-1 7.6-2.8l-3.7-2.8c-1 .7-2.3 1.1-3.9 1.1-3 0-5.5-2-6.4-4.7H1.7v2.9C3.5 20.4 7.4 23 12 23z"/>
      <path fill="#FBBC05" d="M5.6 13.7c-.2-.7-.3-1.4-.3-2.2s.1-1.5.3-2.2V6.4H1.7C.9 8 .5 9.9.5 11.5s.4 3.5 1.2 5.1l3.9-2.9z"/>
      <path fill="#EA4335" d="M12 4.6c1.7 0 3.2.6 4.3 1.7l3.2-3.2C17.7 1.2 15.1 0 12 0 7.4 0 3.5 2.6 1.7 6.4l3.9 2.9c.9-2.7 3.4-4.7 6.4-4.7z"/>
    </svg>
  );
}
